import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import { MessageSquare } from "lucide-react";
import api from "../lib/api";

// Small "text me the app" widget shown below the login form. Non-users
// (arriving via a shared link) can text the app link to their own phone
// to install it later. Rate-limited server-side to 3 sends/day/number.
function TextMeTheApp() {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error("Enter a phone number first");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sms/invite", { phone: phone.trim() });
      toast.success("Sent! Check your texts.");
      setPhone("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={send}
      className="nb-card p-4 space-y-2"
      style={{ background: "#fdecc8" }}
      data-testid="text-me-app-card"
    >
      <div className="flex items-center gap-2">
        <MessageSquare size={14} strokeWidth={3} />
        <div className="font-display font-black text-sm">Text me the app</div>
      </div>
      <p className="text-[11px] font-medium text-[#4a4a4a]">
        Get a text with the link so you can pop it open on your phone.
      </p>
      <div className="flex gap-2">
        <input
          type="tel"
          inputMode="tel"
          data-testid="text-me-phone"
          placeholder="+1 555 123 4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="nb-input flex-1 !py-2 !text-sm"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="text-me-btn"
          className="nb-btn nb-btn-dark !py-2 !px-3 !text-xs"
        >
          {busy ? "Sending…" : "Text me"}
        </button>
      </div>
      <p className="text-[10px] font-medium text-[#4a4a4a]">
        Msg &amp; data rates may apply. Reply STOP to opt out.
      </p>
    </form>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full bg-[#d9d4f1] opacity-70 blur-2xl" />
      <div className="absolute -bottom-24 -right-16 w-80 h-80 rounded-full bg-[#b5e4ca] opacity-60 blur-2xl" />
      <div className="relative w-full max-w-md">
        <div className="mb-6 text-center">
          <img
            src={LOGO_URL}
            alt="SyncBook"
            className="mx-auto w-full max-w-[320px] rounded-2xl border-2 border-[#1a1a1a] shadow-[4px_4px_0_0_#1a1a1a] mb-4"
          />
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
            Welcome back.
          </h1>
          <p className="text-sm text-[#4a4a4a] mt-2 font-medium">
            Balance your checkbook the easy way.
          </p>
        </div>
        <form onSubmit={submit} className="nb-card-lg p-6 space-y-4" data-testid="login-form">
          <div>
            <label className="label">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="nb-input"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="nb-input"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="login-submit-btn"
            disabled={busy}
            className="nb-btn nb-btn-dark w-full !py-3"
            type="submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <Link
              data-testid="forgot-password-link"
              to="/forgot-password"
              className="text-xs font-bold text-[#4a4a4a] underline"
            >
              Forgot password?
            </Link>
          </div>
          <p className="text-center text-sm text-[#4a4a4a] font-medium">
            New here?{" "}
            <Link data-testid="go-register-link" to="/register-account" className="font-bold underline">
              Create an account
            </Link>
          </p>
          <p className="text-center text-[11px] text-[#4a4a4a] font-medium pt-1">
            <Link to="/terms" className="underline font-bold" data-testid="login-terms-link">
              Terms
            </Link>{" "}
            ·{" "}
            <Link
              to="/privacy"
              className="underline font-bold"
              data-testid="login-privacy-link"
            >
              Privacy Policy
            </Link>
          </p>
        </form>
        <div className="mt-4">
          <TextMeTheApp />
        </div>
      </div>
    </div>
  );
}
